/**
 * Opt-in verbose logging for the Monaco / Pyright LSP client.
 *
 * Set `VITE_DEBUG_LSP=true` in `.env` / `.env.development` and restart the dev server (or rebuild).
 *
 * Vite exposes env as strings and statically replaces `import.meta.env.VITE_DEBUG_LSP` at build time,
 * so {@link LSP_DEBUG} collapses to a constant and any `if (LSP_DEBUG) { ... }` block (and its
 * argument expressions) is eliminated by the minifier in production builds.
 *
 * Always gate call sites with `if (LSP_DEBUG)` so argument expressions are not evaluated when debug
 * is off. Prefer variadic calls (e.g. `lspDebugLog('result:', obj)`) over `JSON.stringify` so the
 * DevTools console can pretty-print lazily on large payloads.
 *
 * **Do not confuse** with `import.meta.env.DEV`, which is boolean `true` in development for every app.
 *
 * Note: this module writes directly to `console.debug` rather than routing through the React
 * `LoggingProvider` context. `LspClient` is a plain class instantiated outside the React tree, so
 * it has no access to the context — and these traces are developer-only dev aids, not user-facing
 * telemetry, so bypassing the context is deliberate.
 */
export const LSP_DEBUG: boolean = import.meta.env.VITE_DEBUG_LSP === 'true';

/** Trace-level LSP messages. Always wrap calls in `if (LSP_DEBUG)` so arguments are not evaluated when off. */
export function lspDebugLog(message: string, ...optionalParams: unknown[]): void {
  console.debug(`[LspClient] ${message}`, ...optionalParams);
}
